import { priceSourcesApi, priceSourceUploadsApi } from 'app/services/api';

import { PpeType } from 'constants/company';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { downloadOrShareBlob } from 'utils/nativeFile';

import { getExternalProgramMappingsAction } from 'store/system/actions';

import { extractErrorMessage } from '../marginCenter.actionUtils';
import {
  MATRIX_VERSION_STATUS,
  PRICE_SOURCE_UPLOAD_STATUS,
  UPLOAD_CONFLICT_CODE,
  UPLOAD_LABELS_CHANGED_MESSAGE,
  UPLOAD_TYPE,
} from '../marginCenter.constants';

import { getPriceSourceMatrixVersionsAction } from './priceSourceMatrices.actions';
import {
  getPriceSourceAction,
  getPriceSourceBaseVersionAction,
  getPriceSourceVersionsAction,
} from './priceSources.actions';

export const PRICE_SOURCE_UPLOADS_ACTION_TYPE = createNamedActions(
  'marginCenter/priceSourceUploads',
  {
    SET_LIST_LOADING: 'SET_LIST_LOADING',
    GET_LIST: 'GET_LIST',
    SET_LIST_ERROR: 'SET_LIST_ERROR',
    SET_LATEST: 'SET_LATEST',
    SET_CURRENT_LOADING: 'SET_CURRENT_LOADING',
    GET_CURRENT: 'GET_CURRENT',
    SET_CURRENT_ERROR: 'SET_CURRENT_ERROR',
    CLEAR_CURRENT: 'CLEAR_CURRENT',
    SET_UPLOADING: 'SET_UPLOADING',
    SET_UPLOAD_ERROR: 'SET_UPLOAD_ERROR',
    SET_GRID_LOADING: 'SET_GRID_LOADING',
    GET_GRID: 'GET_GRID',
    SET_GRID_ERROR: 'SET_GRID_ERROR',
    SET_PROPOSED_VERSION_LOADING: 'SET_PROPOSED_VERSION_LOADING',
    GET_PROPOSED_VERSION: 'GET_PROPOSED_VERSION',
    SET_PROPOSED_VERSION_ERROR: 'SET_PROPOSED_VERSION_ERROR',
    SET_PROGRAM_CANDIDATES_LOADING: 'SET_PROGRAM_CANDIDATES_LOADING',
    GET_PROGRAM_CANDIDATES: 'GET_PROGRAM_CANDIDATES',
    SET_PROGRAM_CANDIDATES_ERROR: 'SET_PROGRAM_CANDIDATES_ERROR',
    SET_PROPOSAL_SAVING: 'SET_PROPOSAL_SAVING',
    SET_PROPOSAL_ERROR: 'SET_PROPOSAL_ERROR',
    SET_SUBMITTING: 'SET_SUBMITTING',
    SET_SUBMIT_ERROR: 'SET_SUBMIT_ERROR',
    SET_SUBMIT_RESULT: 'SET_SUBMIT_RESULT',
    CLEAR_SUBMIT: 'CLEAR_SUBMIT',
    SET_APPROVING: 'SET_APPROVING',
    SET_APPROVE_ERROR: 'SET_APPROVE_ERROR',
    SET_APPROVE_RESULT: 'SET_APPROVE_RESULT',
    CLEAR_APPROVAL: 'CLEAR_APPROVAL',
    SET_DISCARDING: 'SET_DISCARDING',
    SET_DISCARD_ERROR: 'SET_DISCARD_ERROR',
    SET_REVIEW_LOADING: 'SET_REVIEW_LOADING',
    GET_REVIEW: 'GET_REVIEW',
    SET_REVIEW_ERROR: 'SET_REVIEW_ERROR',
    SET_OUTLINE_LOADING: 'SET_OUTLINE_LOADING',
    GET_OUTLINE: 'GET_OUTLINE',
    SET_OUTLINE_ERROR: 'SET_OUTLINE_ERROR',
  },
);

const UPLOAD_HISTORY_TAKE = 20;

// Only one matrix version is Active at a time; the newest 50 by effective date always include it.
const MATRIX_VERSIONS_TAKE = 50;

const getStatus = error => error?.response?.status;

const isConflictError = error => getStatus(error) === HTTP_RESPONSE_STATUS_CODES.CONFLICT;

const isBadRequestError = error => getStatus(error) === HTTP_RESPONSE_STATUS_CODES.BAD_REQUEST;

const isNotFoundError = error => getStatus(error) === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND;

const getDuplicateUploadId = error => error?.response?.data?.existingUploadId ?? null;

const getFallbackConflictCode = error => {
  if (extractErrorMessage(error) === UPLOAD_LABELS_CHANGED_MESSAGE) {
    return UPLOAD_CONFLICT_CODE.PROPOSAL_CHANGED;
  }

  return getDuplicateUploadId(error) ? UPLOAD_CONFLICT_CODE.DUPLICATE_UPLOAD : null;
};

const getConflictCode = error =>
  isConflictError(error) ? (error.response.data?.code ?? getFallbackConflictCode(error)) : null;

const isLabelsChangedError = error =>
  getConflictCode(error) === UPLOAD_CONFLICT_CODE.PROPOSAL_CHANGED;

const readUploadModel = async uploadId => {
  try {
    const { data } = await priceSourceUploadsApi.getUpload(uploadId);

    return data ?? null;
  } catch {
    return null;
  }
};

const getLatestUploadModel = uploads => {
  const latest = uploads.find(upload => upload.status !== PRICE_SOURCE_UPLOAD_STATUS.DISCARDED);

  return latest ? readUploadModel(latest.uploadId) : null;
};

export const readPriceSourceUploadAction = uploadId => () => readUploadModel(uploadId);

export const getPriceSourceUploadsAction = priceSourceId => async dispatch => {
  dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_LIST_LOADING, payload: true });

  try {
    const { data } = await priceSourceUploadsApi.getUploads(priceSourceId, {
      take: UPLOAD_HISTORY_TAKE,
    });
    const uploads = data ?? [];
    const latest = await getLatestUploadModel(uploads);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_LATEST, payload: latest });

    return dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_LIST, payload: uploads });
  } catch {
    return dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_LIST_ERROR });
  }
};

// A publish can add LenderV2 program mappings (applyProgramMappings), so the mapping card's list is
// re-read too. allSettled keeps a failed refresh (already reported by its own toast) from turning a
// successful publish into a failed one.
const refreshSourceAfterPublish = (dispatch, priceSourceId) =>
  Promise.allSettled([
    dispatch(getPriceSourceAction(priceSourceId)),
    dispatch(getPriceSourceVersionsAction(priceSourceId)),
    dispatch(getPriceSourceBaseVersionAction(priceSourceId)),
    dispatch(getPriceSourceUploadsAction(priceSourceId)),
    dispatch(getExternalProgramMappingsAction({ pricingEngineType: PpeType.LENDER_V2_PPE })),
  ]);

export const getPriceSourceUploadAction = uploadId => async dispatch => {
  dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_CURRENT_LOADING, payload: true });

  try {
    const { data } = await priceSourceUploadsApi.getUpload(uploadId);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });

    return data;
  } catch {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_CURRENT_ERROR });

    return null;
  }
};

export const clearPriceSourceUploadAction = () => ({
  type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_CURRENT,
});

// An upload that matches its template and every clean-upload rule is published by Pricing during
// the POST, so the source and its versions are refreshed whenever the returned status is Published.
export const uploadPriceSourceFileAction =
  ({ priceSourceId, file, effectiveDate = null, uploadType = UPLOAD_TYPE.RATE_SHEET }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_UPLOADING, payload: true });

    try {
      const { data } = await priceSourceUploadsApi.uploadFile({
        priceSourceId,
        file,
        effectiveDate,
        uploadType,
      });

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });

      await (data?.status === PRICE_SOURCE_UPLOAD_STATUS.PUBLISHED
        ? refreshSourceAfterPublish(dispatch, priceSourceId)
        : dispatch(getPriceSourceUploadsAction(priceSourceId)));

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_UPLOADING, payload: false });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_UPLOAD_ERROR,
        payload: {
          message: extractErrorMessage(error),
          duplicateUploadId:
            getConflictCode(error) === UPLOAD_CONFLICT_CODE.DUPLICATE_UPLOAD
              ? getDuplicateUploadId(error)
              : null,
        },
      });

      return null;
    }
  };

export const getUploadGridAction =
  ({ uploadId, gridId }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_GRID_LOADING, payload: { gridId } });

    try {
      const { data } = await priceSourceUploadsApi.getGrid(uploadId, gridId);

      return dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_GRID,
        payload: { gridId, data },
      });
    } catch {
      return dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_GRID_ERROR,
        payload: { gridId },
      });
    }
  };

// 409 means the proposal still has an unlabelled grid and 400 means a labelled grid can't be read
// (an LLPA band Pricing can't parse); both are kept apart from transport failures so the review
// step can send the user back to labelling instead of offering a retry.
export const getUploadProposedVersionAction = uploadId => async dispatch => {
  dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSED_VERSION_LOADING });

  try {
    const { data } = await priceSourceUploadsApi.getProposedVersion(uploadId);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_PROPOSED_VERSION, payload: data });

    return data;
  } catch (error) {
    dispatch({
      type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSED_VERSION_ERROR,
      payload: {
        message: extractErrorMessage(error),
        isIncomplete: isConflictError(error),
        isUnreadable: isBadRequestError(error),
      },
    });

    return null;
  }
};

export const getUploadProgramCandidatesAction = uploadId => async dispatch => {
  dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROGRAM_CANDIDATES_LOADING });

  try {
    const { data } = await priceSourceUploadsApi.getProgramCandidates(uploadId);

    return dispatch({
      type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_PROGRAM_CANDIDATES,
      payload: data ?? [],
    });
  } catch {
    return dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROGRAM_CANDIDATES_ERROR });
  }
};

export const saveUploadProposalAction =
  ({ uploadId, proposal }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING, payload: true });

    try {
      const { data } = await priceSourceUploadsApi.updateProposal(uploadId, proposal);

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });
      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING, payload: false });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_ERROR,
        payload: extractErrorMessage(error),
      });

      return null;
    }
  };

// LEN-259 phase 6: a correction to an AiFirst upload's reading goes back as an extraction, merged by
// grid key. Pricing re-reads the cells, so a 400 names the cell a typed number is not in.
export const saveUploadExtractionAction =
  ({ uploadId, extraction }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING, payload: true });

    try {
      const { data } = await priceSourceUploadsApi.updateExtraction(uploadId, extraction);

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });
      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING, payload: false });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_ERROR,
        payload: extractErrorMessage(error),
      });

      return null;
    }
  };

// LEN-259 phase 7.2: the outcome gate's review of a closed-set reading (the matrix, the not-priced
// list and what needs a person), kept in the store so the drawer can hold submit back while an item
// waits for a person. A refresh keeps the review shown until the new one arrives.
export const getUploadReviewAction = uploadId => async dispatch => {
  dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_REVIEW_LOADING });

  try {
    const { data } = await priceSourceUploadsApi.getUploadReview(uploadId);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_REVIEW, payload: data ?? null });

    return data ?? null;
  } catch (error) {
    dispatch({
      type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_REVIEW_ERROR,
      payload: extractErrorMessage(error),
    });

    return null;
  }
};

// The outline a closed-set upload was split into. A 404 means it has none yet (the upload has not
// gone to AI reading), which is not an error.
export const getUploadOutlineAction = uploadId => async dispatch => {
  dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_OUTLINE_LOADING });

  try {
    const { data } = await priceSourceUploadsApi.getUploadOutline(uploadId);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_OUTLINE, payload: data ?? null });

    return data ?? null;
  } catch (error) {
    dispatch({
      type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_OUTLINE_ERROR,
      payload: {
        message: isNotFoundError(error) ? null : extractErrorMessage(error),
        isMissing: isNotFoundError(error),
      },
    });

    return null;
  }
};

// LEN-259 phase 7.2 (U21): the coverage map reads a tab's cells and their classes a page at a time,
// so these return the page and keep nothing in the store; errorMessage says why a page failed.
export const readUploadCellsAction =
  ({ uploadId, sheet, range = null, continuation = null }) =>
  async () => {
    try {
      const { data } = await priceSourceUploadsApi.getUploadCells(
        uploadId,
        sheet,
        range,
        continuation,
      );

      return { data: data ?? null, errorMessage: null };
    } catch (error) {
      return { data: null, errorMessage: extractErrorMessage(error) };
    }
  };

export const readUploadCoverageAction =
  ({ uploadId, sheet, continuation = null }) =>
  async () => {
    try {
      const { data } = await priceSourceUploadsApi.getUploadReview(uploadId, sheet, continuation);

      return { data: data?.coverage ?? null, errorMessage: null };
    } catch (error) {
      return { data: null, errorMessage: extractErrorMessage(error) };
    }
  };

// A person's change to a closed-set reading (Source Manual): products for a grid, a block answered
// not priced or priced, or a label's tags. Pricing re-derives every scope from the tags and
// re-evaluates the upload, so the review and outline are read again; a 400 names what it refused.
export const saveUploadReadingAction =
  ({ uploadId, reading }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING, payload: true });

    try {
      const { data } = await priceSourceUploadsApi.saveUploadReading(uploadId, reading);

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });
      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_SAVING, payload: false });
      await Promise.allSettled([
        dispatch(getUploadReviewAction(uploadId)),
        dispatch(getUploadOutlineAction(uploadId)),
      ]);

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_ERROR,
        payload: extractErrorMessage(error),
      });

      return null;
    }
  };

// Starts (or retries) the AI reading Pricing runs for an AiFirst upload, or reads it again: only the
// grids in gridKeys when given. Idempotent on Pricing's side: while a run reads the upload it comes
// back unchanged.
export const startUploadAiReadingAction = (uploadId, gridKeys) => async dispatch => {
  try {
    const { data } = await priceSourceUploadsApi.startAiReading(uploadId, gridKeys);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });

    return { upload: data, errorMessage: null };
  } catch (error) {
    return { upload: null, errorMessage: extractErrorMessage(error) };
  }
};

// "Read with today's parser": Pricing stages the grid detector's grids from the upload's cells for
// labelling by hand, replacing what the AI or the template read.
export const readUploadWithParserAction = uploadId => async dispatch => {
  try {
    const { data } = await priceSourceUploadsApi.readWithParser(uploadId);

    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });

    return { upload: data, errorMessage: null };
  } catch (error) {
    return { upload: null, errorMessage: extractErrorMessage(error) };
  }
};

// Re-reads the open upload without its loading flag, so a poll while the AI reads never flickers the
// drawer. A failed poll keeps what is shown; the next one tries again.
export const refreshPriceSourceUploadAction = uploadId => async dispatch => {
  const data = await readUploadModel(uploadId);

  if (data) {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });
  }

  return data;
};

export const readUploadWorkbookAction = uploadId => async () => {
  try {
    const { data } = await priceSourceUploadsApi.getWorkbook(uploadId);

    return data ?? null;
  } catch {
    return null;
  }
};

// A matrix upload is submitted against the serving (Active) matrix version, not the rate-sheet version.
const readServingMatrixVersionId = async priceSourceId => {
  const { data } = await priceSourcesApi.getMatrixVersions(priceSourceId, {
    take: MATRIX_VERSIONS_TAKE,
  });

  return (
    (data ?? []).find(version => version.status === MATRIX_VERSION_STATUS.ACTIVE)?.versionId ?? null
  );
};

const resolveSubmitRequest = async ({ priceSourceId, request, isMatrixUpload }) =>
  isMatrixUpload
    ? { ...request, basedOnVersionId: await readServingMatrixVersionId(priceSourceId) }
    : request;

// The result is dispatched after the refetches settle so the source page never shows the new
// version's banner next to the previous serving summary. A 409 means another version landed after
// the review started; the diff has to be re-read before anyone submits again. A ProposalChanged 409
// (an AI run or another tab saved new labels) re-reads the upload and resolves with isLabelsChanged
// so the wizard can send the user back to review.
export const submitPriceSourceUploadAction =
  ({ priceSourceId, uploadId, request, isMatrixUpload = false }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_SUBMITTING });

    try {
      const { data } = await priceSourceUploadsApi.submit(
        uploadId,
        await resolveSubmitRequest({ priceSourceId, request, isMatrixUpload }),
      );

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data?.upload });
      await refreshSourceAfterPublish(dispatch, priceSourceId);

      if (isMatrixUpload) {
        await dispatch(getPriceSourceMatrixVersionsAction(priceSourceId));
      }

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_SUBMIT_RESULT, payload: data });

      return data;
    } catch (error) {
      if (isLabelsChangedError(error)) {
        dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_SUBMIT });

        const upload = await dispatch(getPriceSourceUploadAction(uploadId));

        return { upload, version: null, isLabelsChanged: true };
      }

      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_SUBMIT_ERROR,
        payload: {
          message: extractErrorMessage(error),
          isConflict: isConflictError(error),
          conflictCode: getConflictCode(error),
        },
      });

      return null;
    }
  };

export const clearUploadProposalErrorAction = () => ({
  type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_PROPOSAL_ERROR,
  payload: null,
});

export const clearUploadSubmitAction = () => ({
  type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_SUBMIT,
});

export const approvePriceSourceUploadAction =
  ({ priceSourceId, uploadId, basedOnVersionId }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_APPROVING, payload: { uploadId } });

    try {
      const { data } = await priceSourceUploadsApi.approve(uploadId, {
        basedOnVersionId: basedOnVersionId ?? null,
      });

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data?.upload });
      await refreshSourceAfterPublish(dispatch, priceSourceId);
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_APPROVE_RESULT,
        payload: { uploadId, result: data },
      });

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_APPROVE_ERROR,
        payload: {
          uploadId,
          message: extractErrorMessage(error),
          isConflict: isConflictError(error),
          conflictCode: getConflictCode(error),
        },
      });

      return null;
    }
  };

export const clearUploadApprovalAction = () => ({
  type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.CLEAR_APPROVAL,
});

export const discardPriceSourceUploadAction =
  ({ priceSourceId, uploadId }) =>
  async dispatch => {
    dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_DISCARDING, payload: true });

    try {
      const { data } = await priceSourceUploadsApi.discard(uploadId);

      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.GET_CURRENT, payload: data });
      dispatch({ type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_DISCARDING, payload: false });
      await dispatch(getPriceSourceUploadsAction(priceSourceId));

      return data;
    } catch (error) {
      dispatch({
        type: PRICE_SOURCE_UPLOADS_ACTION_TYPE.SET_DISCARD_ERROR,
        payload: extractErrorMessage(error),
      });

      return null;
    }
  };

export const downloadPriceSourceUploadFileAction = catchAsyncActionError(
  ({ uploadId, fileName }) =>
    async () => {
      const { data } = await priceSourceUploadsApi.getFile(uploadId);

      return downloadOrShareBlob(data, fileName);
    },
);
